import { Tabs as UITabs, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trigger, List } from "@radix-ui/react-tabs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { clientOnboardingSchema, clientOnboardingType } from "./validator";
import { Form } from "@/components/ui/form";
import {
  FinalStepContent,
  FinalStepContentPreview,
  Step1Content,
  Step1ContentPreview,
  Step2Content,
  Step2ContentPreview,
  Step3Content,
  Step3ContentPreview,
  Step4Content,
  Step4ContentPreview,
} from "./components";
// import { ChevronLeft } from "lucide-react";
import LogoIcon from "@/components/icons/LogoIcon";
import useCurrentUserStore from "@/store";

const OnBoarding: React.FC = () => {
  const { appId, generatedBlog, isOnboardingComplete } = useCurrentUserStore();
  const [step, setStep] = useState(
    generatedBlog.length > 0 ? 4 : appId !== "" ? 3 : 1
  );

  useEffect(() => {
    if (isOnboardingComplete && step !== 5) {
      setStep(5);
    }
  }, [isOnboardingComplete, step]);

  const randomNumber = Math.floor(Math.random() * 1000);

  const myAppName = `my-app-${randomNumber}`;

  const form = useForm<clientOnboardingType>({
    resolver: zodResolver(clientOnboardingSchema),
    defaultValues: {
      step1: {
        appName: "",
        appTemplateId: "",
        appTemplateName: "",
        requiredTemplateModalTypes: [],
      },
      step2: {
        appName: myAppName,
        slug: myAppName,
        appUrl: `https://${myAppName}`,
        description: "",
        is_paid: false,
        text_to_text_modal: undefined,
        text_to_image_modal: undefined,
        ai_models: [],
      },
      step3: {
        blogTitle: "",
        blogContent: "",
        //         blogContent: `In today's fast-paced digital landscape, content marketing has become an essential strategy for businesses to reach their target audience and drive growth. However, creating high-quality content consistently can be time-consuming and resource-intensive. This is where artificial intelligence (AI) can make a significant difference. AI-powered blog campaigns offer a multitude of benefits that can streamline your content creation process and improve your overall marketing efforts.

        // ### Benefits of AI-Based Blog Campaigns:

        // #### Enhanced Efficiency:

        // * **Automated Content Generation:** AI can generate blog posts, headlines, and even social media captions based on your input. This saves time and effort, allowing you to focus on other aspects of your business.
        // * **Personalized Content Recommendations:** AI algorithms can analyze your target audience's preferences and interests to suggest relevant topics and keywords for your blog posts. This ensures that your content resonates with your readers.
        // * **Optimized Content Scheduling:** AI can help you schedule your blog posts at the optimal times to maximize engagement and reach.

        // #### Improved Content Quality:

        // * **Natural Language Processing (NLP):** AI-powered NLP tools can analyze your content for clarity, coherence, and readability. This helps you create high-quality content that is easy to understand and engaging.
        // * **SEO Optimization:** AI can identify relevant keywords and optimize your content for search engines, improving your visibility and organic traffic.
        // * **Grammar and Spelling Checks:** AI can catch errors in grammar, spelling, and punctuation, ensuring that your content is error-free.

        // #### Data-Driven Insights:

        // * **Performance Analytics:** AI can track the performance of your blog campaigns, providing valuable insights into what works and what doesn't. This allows you to make data-driven decisions and optimize your strategy.
        // * **Audience Segmentation:** AI can help you segment your audience based on demographics, interests, and behavior, enabling you to tailor your content to specific groups.
        // * **Predictive Analytics:** AI can predict future trends and anticipate your audience's needs, helping you stay ahead of the curve.

        // #### Cost-Effective Solutions:

        // * **Reduced Labor Costs:** AI can automate many tasks, reducing the need for manual labor and saving you money.
        // * **Increased ROI:** By creating high-quality content that resonates with your target audience, AI-powered blog campaigns can help you generate a higher return on investment.

        // ### Example of AI-Generated Content:

        // > **Prompt:** Write a blog post about the benefits of using AI in content marketing.

        // > **Generated Content:**
        // > Artificial intelligence (AI) has revolutionized the way businesses approach content marketing. By automating tasks, enhancing content quality, providing data-driven insights, and offering cost-effective solutions, AI can help you create more effective and engaging content that drives results. From generating blog posts to optimizing SEO, AI can streamline your content creation process and improve your overall marketing strategy.

        // ### Comparison of AI-Powered and Manually Created Content:

        // | Feature | AI-Powered Content | Manually Created Content |
        // |---|---|---|
        // | Speed | Faster | Slower |
        // | Consistency | More consistent | Less consistent |
        // | Scalability | Easily scalable | Difficult to scale |
        // | Cost | Lower | Higher |
        // | Quality | High quality with proper training | Depends on writer's skill |

        // ### Conclusion

        // AI-powered blog campaigns offer a powerful tool for businesses looking to improve their content marketing efforts. By automating tasks, enhancing content quality, providing data-driven insights, and offering cost-effective solutions, AI can help you create more effective and engaging content that drives results. If you're not already using AI in your blog campaigns, now is the time to start exploring the possibilities.`,
      },
      step4: {
        primaryColor: "#000000",
          backgroundColor: "#FFFFFF",
        appearance: "light",
        bodyFont: "Poppins",
        headerFont: "Outfit",
        logo: "",
      },
    },
  });

  const onSubmit = () => {};

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <main className="flex flex-col-reverse lg:flex-row max-w-[99vw]">
          <div className="flex flex-col gap-4 lg:w-[40%] lg:p-2">
            {/* Logo */}
            <LogoIcon />
            {/* Tab List */}
            <div className="flex flex-col">
              <UITabs value={step.toString()}>
                <List
                  className={cn("flex items-center gap-2 px-9 mt-6", {
                    "mx-1": isOnboardingComplete,
                  })}
                >
                  {/* <ChevronLeft
                    className={cn("mr-2 cursor-pointer", {
                      "opacity-0": step === 1,
                      hidden: isOnboardingComplete,
                    })}
                    onClick={() => setStep(step - 1)}
                  /> */}
                  {Array.from({ length: 4 }).map((_, index) => {
                    const idx = index;
                    return (
                      <Trigger
                        key={idx}
                        value={(idx + 1).toString()}
                        disabled={
                          isOnboardingComplete ? idx + 1 < 5 : step <= idx + 1
                        }
                        className={cn("w-8 h-1 rounded-full bg-secondary", {
                          "bg-primary": step > idx,
                        })}
                        onClick={() => setStep(idx + 1)}
                      />
                    );
                  })}
                </List>

                <TabsContent
                  value="1"
                  className="px-9 focus-visible:ring-0 max-w-[98vw]"
                >
                  <Step1Content form={form} nextTrigger={() => setStep(2)} />
                </TabsContent>
                <TabsContent
                  value="2"
                  className="px-9 focus-visible:ring-0 max-w-[98vw]"
                >
                  <Step2Content form={form} nextTrigger={() => setStep(3)} />
                </TabsContent>
                <TabsContent
                  value="3"
                  className="px-9 focus-visible:ring-0 max-w-[98vw]"
                >
                  <Step3Content form={form} nextTrigger={() => setStep(4)} />
                </TabsContent>
                <TabsContent
                  value="4"
                  className="px-9 focus-visible:ring-0 max-w-[98vw]"
                >
                  <Step4Content form={form} nextTrigger={() => setStep(5)} />
                </TabsContent>
                <TabsContent
                  value="5"
                  className="px-9 focus-visible:ring-0 max-w-[98vw]"
                >
                  <FinalStepContent />
                </TabsContent>
              </UITabs>
            </div>
          </div>

          <div className="lg:w-[60%] lg:h-screen">
            {step === 1 && <Step1ContentPreview form={form} />}
            {step === 2 && <Step2ContentPreview form={form} />}
            {step === 3 && <Step3ContentPreview form={form} />}
            {step === 4 && <Step4ContentPreview form={form} />}
            {step === 5 && <FinalStepContentPreview />}
          </div>
        </main>
      </form>
    </Form>
  );
};

export default OnBoarding;
